import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["centered", "children", "use", "headerCallback", "size", "isIFramed"];
// temporary until migration is complete
import { useContext } from 'react';
import styled from 'foundations-theming/utils';
import { toPx } from '../../../utils/toPx';
import { DialogContext, DialogContextProvider } from '../Dialog/DialogContext';
import { StyledHeaderInner } from '../DialogHeader/DialogHeader';
import emptyFunction from 'react-utils/emptyFunction';
import { StyledDialogBody } from '../DialogBody/DialogBody';
import { getBackwardsCompatibilityClasses } from './internals/utils';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { StyledForm } from '../Form/Form';
import { StyledOverlayElement } from '../Overlay/Overlay';
import { jsx as _jsx } from "react/jsx-runtime";
const GRID_BREAKPOINT_SMALL = '544px';
const ABOVE_MOBILE_BREAKPOINT = toPx(parseInt(GRID_BREAKPOINT_SMALL, 10) + 1);
const FullscreenStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '100%',
  borderRadius: 0
};
const SidebarStyles = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  borderRadius: 0,
  minWidth: '300px',
  maxWidth: '100%'
};
const LightboxStyles = {
  bottom: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  display: 'block',
  height: '100%',
  overflow: 'hidden',
  width: '100%'
};
const sizeToHeightMap = {
  auto: undefined,
  medium: 408,
  short: 250,
  tall: 700
};

/**
 * Generates background styling for dialog headers based on variant and context.
 * @param isRootWrapper - Whether styling is applied to root wrapper vs header element
 * @param theme - The theme object containing color tokens
 * @param use - The dialog variant (default, sidebar, conversational, etc.)
 */
export const getDialogHeaderBackground = ({
  isRootWrapper = false,
  theme,
  use
}) => {
  const isDefault = use === 'default';
  const isSidebar = use === 'sidebar';
  const isConversational = use === 'conversational';
  const isDanger = use === 'danger';
  const isUpgrades = use === 'upgrades';
  const isLightbox = use === 'lightbox';
  const isFullscreen = use === 'fullscreen';
  const shouldUseRootWrapperBackground = isRootWrapper && (isDefault || isSidebar);
  const getBackgroundColor = () => {
    if (shouldUseRootWrapperBackground) {
      return theme.color['fill-surface-default'];
    }
    if (isLightbox) {
      return theme.color['fill-transparent-default'];
    }
    if (!isConversational && !isFullscreen) {
      return theme.color['fill-info-default'];
    }
    return theme.color['fill-surface-default'];
  };
  const getBackgroundImage = () => {
    if (isDanger) {
      return `linear-gradient(135deg, ${theme.color['fill-transitional-danger-pattern-color-1']}, ${theme.color['fill-transitional-danger-pattern-color-2']})`;
    }
    if (isUpgrades) {
      return `linear-gradient(-180deg, ${theme.color['fill-transitional-upgrade-modal-gradient-color-1']} 20%, ${theme.color['fill-transitional-upgrade-modal-gradient-color-1']} 71%)`;
    }
    if (isLightbox || isFullscreen || shouldUseRootWrapperBackground) {
      return undefined;
    }
    if (!isConversational) {
      return `linear-gradient(86deg, ${theme.color['fill-transitional-modal-gradient-color-1']} -3.28%, ${theme.color['fill-transitional-modal-gradient-color-2']} 97.8%)`;
    }
    return undefined;
  };
  return {
    backgroundColor: getBackgroundColor(),
    backgroundImage: getBackgroundImage()
  };
};
export const StyledDialog = styled.div.withConfig({
  displayName: "Dialog__StyledDialog",
  componentId: "sc-10bggi2-0"
})(({
  theme,
  _dialogWidth,
  headerOverflow,
  $centered,
  $isIFramed,
  $size,
  $cssStyles
}) => {
  const modalUse = useContext(DialogContext).use;
  const isDefault = modalUse === 'default';
  const isFullscreen = modalUse === 'fullscreen';
  const isSidebar = modalUse === 'sidebar';
  const isLightbox = modalUse === 'lightbox';
  const isBordered = !$isIFramed && !isFullscreen;
  const borderRadius = isFullscreen || isSidebar ? theme.borderRadius['0'] : theme.borderRadius['transitional-floating'];
  return Object.assign({
    textAlign: $centered ? 'center' : undefined,
    justifyContent: $centered ? 'center' : undefined,
    [`${StyledHeaderInner}`]: {
      justifyContent: $centered ? 'center !important' : undefined
    },
    [`${StyledDialogBody}`]: Object.assign({}, $size && {
      minHeight: sizeToHeightMap[$size]
    }),
    border: isBordered ? `${theme.borderWidth['transitional-2']} solid ${theme.color['border-transitional-core-subtle-alt']}` : undefined,
    borderRadius,
    boxShadow: theme.boxShadow['300'],
    position: 'relative'
  }, isFullscreen && FullscreenStyles, isSidebar && SidebarStyles, isLightbox && Object.assign({}, LightboxStyles, {
    color: theme.color['text-core-on-fill-default']
  }), {
    display: 'flex',
    flexDirection: 'column'
  }, !isLightbox && _dialogWidth && {
    width: toPx(_dialogWidth)
  }, getDialogHeaderBackground({
    theme,
    use: modalUse,
    isRootWrapper: true
  }), {
    [`& > ${StyledForm}`]: {
      flex: '1 1 0%',
      minHeight: 'auto',
      overflow: 'auto'
    },
    '&': {
      [`@media only screen and (max-width: ${ABOVE_MOBILE_BREAKPOINT})`]: {
        height: '100%'
      }
    },
    [`& > :first-child`]: {
      borderStartStartRadius: borderRadius,
      borderStartEndRadius: borderRadius
    },
    [`& > :last-child`]: {
      borderEndStartRadius: borderRadius,
      borderEndEndRadius: borderRadius
    },
    [`& > form > :first-child`]: {
      borderStartStartRadius: borderRadius,
      borderStartEndRadius: borderRadius,
      [`@media only screen and (max-width: ${ABOVE_MOBILE_BREAKPOINT})`]: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }
    },
    [`& > form > :last-child`]: {
      borderEndStartRadius: borderRadius,
      borderEndEndRadius: borderRadius
    },
    // A bit of a hack, but this is to get the absolutely positioned loading element overlay to inherit
    // the border radius and prevent spill over the corner of the dialog
    [`& ${StyledOverlayElement}`]: {
      borderRadius
    },
    [`@media only screen and (min-width: ${ABOVE_MOBILE_BREAKPOINT})`]: {
      top: headerOverflow && toPx(headerOverflow)
    }
  }, isDefault && {
    // eslint-disable-next-line hubspot-dev/no-private-classes
    [`&.private-modal--default .uiDialogBody`]: {
      // Prevent the background image definition from leaking into the legacy component body
      // @see https://git.hubteam.com/HubSpot/foundations-components/issues/3817
      background: theme.color['fill-surface-default']
    }
  }, $cssStyles && $cssStyles);
});

/**
 * For more details about this component please check the [Storybook page](https://private.hubteamqa.com/foundations-components-storybook/?path=/docs/transitional-dialog--page).
 */
export const Dialog = _ref => {
  let {
      centered = false,
      children,
      use = 'default',
      headerCallback = emptyFunction,
      size = 'medium',
      isIFramed = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const classNames = getBackwardsCompatibilityClasses(use, size, children);
  const {
    domProps,
    tokenProps,
    restProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(DialogContextProvider, {
    value: {
      inDialog: true,
      use,
      headerCallback
    },
    children: /*#__PURE__*/_jsx(StyledDialog, Object.assign({
      $centered: centered,
      $cssStyles: cssStyles,
      $isIFramed: isIFramed,
      $size: size
    }, domProps, restProps, {
      className: classNames,
      children: children
    }))
  });
};